import { useEffect, useState } from 'react';
import { ApiError, api, formatDateId } from '../api/client';
import type { ReceiptDetail } from '../api/types';
import { RECEIPT_STATUS_LABEL, ReceiptStatus } from '../api/enums';
import { Alert, Button, Spinner } from './ui';
import { useT } from '../i18n/locale';

/**
 * A4 Good Receive Note, matching the layout the warehouse already signs:
 * header block, two-column metadata, line table, two signature boxes.
 *
 * Rendered as a full-screen overlay; @media print in index.css hides the app
 * chrome so only the document reaches the paper.
 */
export default function GrnPrint({
  receiptId,
  onClose,
}: {
  receiptId: string;
  onClose: () => void;
}) {
  const t = useT();
  const [data, setData] = useState<ReceiptDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ReceiptDetail>(`/receipts/${receiptId}`)
      .then((d) => {
        /*
         * Recheck on the fetched record, not only where the button is drawn.
         *
         * The status may have moved on since the list was loaded, and a GRN is
         * a document somebody signs for goods: it must assert that Accurate
         * holds the receipt at the moment it is printed, not that it did when
         * the page rendered.
         */
        if (d.receipt.status !== ReceiptStatus.Posted) {
          setError(
            t('grn.notPrintable', {
              number: d.receipt.grNumber,
              status: t(RECEIPT_STATUS_LABEL[d.receipt.status].key),
            }),
          );
          return;
        }
        setData(d);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : t('grn.loadFailed')));
  }, [receiptId]);

  // Name the downloaded PDF after the GR number instead of the page title.
  useEffect(() => {
    if (!data) return;
    const original = document.title;
    document.title = `GRN_${data.receipt.grNumber.replace(/\//g, '_')}`;
    return () => {
      document.title = original;
    };
  }, [data]);

  return (
    /*
     * grn-sheet, and the print: resets, are what make a long receipt print in
     * full. On screen this is a fixed overlay covering the app; a fixed
     * element paints once and is clipped to the first page, so every row past
     * the first page was simply dropped — the document looked complete and
     * was not. In print it goes back into normal flow and paginates.
     */
    <div className="grn-sheet fixed inset-0 z-50 overflow-auto bg-slate-600/70 p-4 print:static print:inset-auto print:z-auto print:overflow-visible print:bg-white print:p-0">
      <div className="mx-auto max-w-4xl print:max-w-none">
        <div className="no-print mb-3 flex items-center justify-between rounded-lg bg-white p-3 shadow-lg">
          <p className="m-0 text-xs text-slate-500">
            {t('grn.printTip', { option: 'Headers and footers' })}
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.close')}
            </Button>
            <Button onClick={() => window.print()} disabled={!data}>
              {t('grn.print')}
            </Button>
          </div>
        </div>

        <div className="bg-white p-8 shadow-xl print:p-0 print:shadow-none">
          {error ? <Alert tone="error">{error}</Alert> : null}
          {!data && !error ? <Spinner label={t('grn.preparing')} /> : null}

          {data ? (
            <>
              <div className="flex items-start justify-between border-b-2 border-slate-800 pb-3">
                <div>
                  <div className="text-lg font-black text-slate-900">{data.receipt.databaseAlias}</div>
                  <div className="text-[11px] text-slate-600">{t('grn.subtitle')}</div>
                </div>
                <div className="rounded border border-slate-800 px-3 py-1 text-[11px] font-bold">
                  {t('grn.badge')}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-6 text-[11px]">
                <dl className="m-0 space-y-1">
                  <Row label={t('grn.vendor')} value={data.receipt.vendorName ?? '-'} />
                  <Row label={t('grn.poNumber')} value={data.receipt.poNumber} />
                  <Row label={t('grn.notes')} value={data.receipt.notes || '-'} />
                </dl>
                <dl className="m-0 space-y-1">
                  <Row label={t('grn.grNumber')} value={data.receipt.grNumber} />
                  <Row label={t('grn.date')} value={formatDateId(data.receipt.date)} />
                  <Row label={t('grn.deliveryNote')} value={data.receipt.deliveryNoteNo} />
                  <Row label={t('grn.warehouse')} value={data.receipt.warehouseName} />
                </dl>
              </div>

              <table className="mt-5 w-full border-collapse text-[10px]">
                <thead>
                  {/*
                    Repeated on every printed page by the browser, which is
                    why the GR number lives here rather than only in the
                    header block: a signed multi-page receipt gets split up in
                    a filing cabinet, and a loose page that cannot be tied
                    back to its document is not much of a record.
                  */}
                  <tr className="print-only bg-white">
                    <Th colSpan={7} className="!border-0 !px-0 !pb-1 text-left text-[9px] font-normal text-slate-500">
                      {data.receipt.grNumber} · {data.receipt.databaseAlias} · {t('grn.continued')}
                    </Th>
                  </tr>
                  <tr className="bg-slate-100">
                    <Th className="w-[5%] text-center">{t('grn.colNo')}</Th>
                    <Th className="w-[12%]">{t('grn.colSku')}</Th>
                    <Th className="w-[13%]">{t('grn.colBarcode')}</Th>
                    <Th className="w-[30%]">{t('grn.colItem')}</Th>
                    <Th className="w-[10%] text-center">{t('grn.colQty')}</Th>
                    <Th className="w-[10%] text-center">{t('grn.colUnit')}</Th>
                    <Th className="w-[20%]">{t('grn.colNotes')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l, i) => (
                    <tr key={i}>
                      <Td className="text-center">{i + 1}</Td>
                      <Td>{l.sku || '-'}</Td>
                      <Td>{l.barcode || '-'}</Td>
                      <Td>{l.itemName}</Td>
                      <Td className="text-center font-bold">{l.qtyReceived}</Td>
                      <Td className="text-center">{l.unitName}</Td>
                      <Td>{l.lineNotes || '-'}</Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <Td colSpan={4} className="text-right">
                      {t('grn.total')}
                    </Td>
                    <Td className="text-center">
                      {data.lines.reduce((s, l) => s + l.qtyReceived, 0)}
                    </Td>
                    <Td colSpan={2} />
                  </tr>
                </tfoot>
              </table>

              {/* Kept whole: a signature box split across a page break is
                  not something anyone should be asked to sign. */}
              <div className="grn-signatures mt-10 grid grid-cols-2 gap-16 text-[10px]">
                <Signature title={t('grn.receivedBy')} subtitle={t('grn.receivedBySub')} />
                <Signature title={t('grn.deliveredBy')} subtitle={t('grn.deliveredBySub')} />
              </div>

              <p className="mt-6 text-[9px] text-slate-400">
                {t('grn.createdBy', { name: data.receipt.createdByName })}
                {data.receipt.aolNumber
                  ? ` · ${t('grn.accurateNo', { number: data.receipt.aolNumber })}`
                  : ''}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1">
      <dt className="m-0 w-40 shrink-0 font-semibold text-slate-600">{label}</dt>
      <dd className="m-0 font-bold text-slate-900">: {value}</dd>
    </div>
  );
}

function Th({
  children,
  className = '',
  colSpan,
}: {
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <th
      colSpan={colSpan}
      className={`border border-slate-400 px-1.5 py-1 text-left font-bold ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
  colSpan,
}: {
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td colSpan={colSpan} className={`border border-slate-300 px-1.5 py-1 align-top ${className}`}>
      {children}
    </td>
  );
}

function Signature({ title, subtitle }: { title: string; subtitle: string }) {
  const t = useT();
  return (
    <div>
      <div className="font-bold">
        {title}
        <br />
        <span className="font-normal text-slate-500">{subtitle}</span>
      </div>
      <div className="mt-20 border-b border-slate-800" />
      <div className="mt-1 flex flex-col gap-0.5 text-slate-600">
        <span>{t('grn.signName')}&nbsp;&nbsp;&nbsp;&nbsp;:</span>
        <span>{t('grn.signDate')} :</span>
      </div>
    </div>
  );
}
